'use client';

import React from 'react';
import { CloseIcon, ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '@wolfejs/icons/IconMap';
import { Button } from '../Button';
import { useTheme } from '../hooks/useTheme';
import { UIStyling } from '../styles/UIStyling';
import type { UIThemableComponent } from '../types/UI';
import { UIColorContext, UIColorStates, UIShapes, UISizes, UIVariants } from '../types/UI';
import { cn } from '../utils/classnames';

export type AlertProps = UIThemableComponent & {
  ref?: React.ForwardedRef<HTMLDivElement>;

  // ----- options
  dismissable?: boolean;
  accented?: boolean;
  fullWidth?: boolean;
  title?: React.ReactNode;
  Icon?: React.ReactNode | 'auto';
  append?: React.ReactNode;

  onClose?: () => void;
};

const AlertComp = (
  {
    children,
    className,

    // ----- Themable props defaults
    color = UIColorContext.default,
    variant = UIVariants.solid,
    shape = UIShapes.rounded,
    size = UISizes.md,
    colorShadows,
    gradient,
    elevation,

    // ----- options
    append,
    accented,
    fullWidth = true,
    dismissable,
    title,
    Icon,

    // ----- slots
    classNames,

    onClose,
    ...otherProps
  }: AlertProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
) => {
  const cname = 'ui-alert';
  const { theme } = useTheme();

  const [closed, setClosed] = React.useState<boolean>(false);

  const handleClose = React.useCallback(() => {
    setClosed(true);
    onClose?.();
  }, [onClose]);

  const classes = React.useMemo(() => {
    let classes = UIVariants.unstyled.toString();

    const themableProps = { variant, color, shape, size, theme, gradient, elevation, accented };

    if (variant !== UIVariants.unstyled) {
      classes = cn(
        cname,
        'inline-flex text-sm',
        // ----- Add standard set of styling classes
        // UIStyling.variants(themableProps),
        UIStyling.shapes(themableProps),
        // UIStyling.textSizes(themableProps),
        UIStyling.elevations(themableProps),
        colorShadows && UIStyling.colorShadows(themableProps),

        {
          // ----- Paddings for various sizes
          [`size-xs p-2`]: size === UISizes.xs,
          [`size-sm p-3 `]: size === UISizes.sm,
          [`size-md p-4`]: size === UISizes.md,
          [`size-lg p-4`]: size === UISizes.lg,
          [`size-xl p-4`]: size === UISizes.xl,

          ['text-xs']: size === UISizes.xs || size == UISizes.sm,
          ['text-sm']: size == UISizes.md,
          ['text-md']: size == UISizes.lg,
          ['text-lg']: size == UISizes.xl,

          // ----- Others
          [`flex`]: fullWidth,

          [`border-t-4 border-${color}-border`]: accented,

          [`border border-transparent`]:
            (variant === UIVariants.solid || variant === UIVariants.outline || variant === UIVariants.ghost) &&
            !accented,

          // Outline alert
          [`border border-${color}`]: variant === UIVariants.outline,
        },

        theme === 'flowbite' && {
          // Solid components have a background and a transparent border
          [`bg-${color}-50 dark:bg-default-800`]: variant === UIVariants.solid && !gradient,

          [`text-${color}-800 dark:text-${color}-400`]: !gradient,

          [`bg-gradient-to-tr from-default-300 via-default-200 to-default-100 dark:from-default-900 dark:via-default-800 dark:to-default-700`]:
            gradient,
        },

        theme !== 'flowbite' && {
          // Solid components have a background and a transparent border
          [`bg-${color}-50 text-${color}-800 dark:bg-${color}-800 dark:text-${color}-400`]:
            (variant === UIVariants.solid || variant === UIVariants.outline) && !gradient,
        },
        className
      );

      return classes;
    }
  }, [accented, className, color, colorShadows, elevation, fullWidth, gradient, shape, size, theme, variant]);

  if (closed) return null;

  const commonProps = {
    ref: forwardedRef,
    className: classes,

    ...otherProps,
  };

  if (Icon === 'auto') {
    if (color === 'info') Icon = <InfoIcon />;
    if (color === 'error') Icon = <ErrorIcon />;
    if (color === 'warning') Icon = <WarningIcon />;
    if (color === 'success') Icon = <SuccessIcon />;
    if (Icon === 'auto') Icon = null;
  }

  return (
    <div {...commonProps}>
      <div className={cn('flex flex-1 items-center justify-between', classNames?.layout)}>
        <div className={cn('flex flex-1 items-center gap-2', classNames?.contentWrapper)}>
          {Icon && <div className={cn('[&>svg]:h-4 [&>svg]:w-4', classNames?.icon)}>{Icon}</div>}
          <div className={cn('block', classNames?.content)}>
            {title && <div className={cn('text-lg font-bold', classNames?.title)}>{title}</div>}
            {children && <div className={cn(classNames?.label)}>{children}</div>}
          </div>
        </div>
      </div>
      {append && <div className={cn('ml-4 flex gap-2', classNames?.append)}>{append}</div>}
      {dismissable && (
        <div className={cn('ml-4 flex cursor-pointer gap-2', classNames?.dismissable)} onClick={handleClose}>
          <Button className="-mx-1.5 -my-1.5 h-8 w-8 p-1.5" variant={UIVariants.ghost} square>
            <CloseIcon />
          </Button>
        </div>
      )}
    </div>
  );
};

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  AlertComp
) as React.ForwardRefExoticComponentExtended<AlertProps>;
Alert.displayName = 'Alert';

export const AlertColors = [UIColorContext.default, ...Object.values(UIColorStates)];
